/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import moment from 'moment-timezone';
import Modal from 'common/components/modal/ModalRelative';
import { displayDate, displayTimeWithTimezone } from 'common/utils/date-time';
import { getDistanceFromLatLonInMile, toDMS } from 'common/utils/Utils';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { WorkOrder, Quote } from 'common/types';
import {
    Content,
    Row,
    Label,
    Value,
    Title,
    ModalWrap,
    ModalHeader,
} from './MetadataModal';

interface VideoMetadataModalProps {
    metadata?: {
        metadata: {
            timezoneId?: string;
            duration?: number;
            format?: string;
            height?: number;
            width?: number;
            latitude?: number;
            longitude?: number;
            brand?: string;
            model?: string;
            createDate?: string;
        };
    };
    document: WorkOrder | Quote;
    variant: 'modal' | 'drawer';
    handleCloseOnClick: () => void;
}

export default function VideoMetadataModal(props: VideoMetadataModalProps) {
    const metadata = props.metadata?.metadata || {};
    const variant = props.variant || 'drawer';
    const noValue = 'N/A';
    const timezoneId =
        metadata.timezoneId || props.document?.property?.timezone || 'UTC';
    let withInRadius;
    if (
        metadata.latitude &&
        metadata.longitude &&
        props.document.property.standardized &&
        props.document.property.latitude &&
        props.document.property.longitude
    ) {
        withInRadius =
            getDistanceFromLatLonInMile(
                metadata.latitude,
                metadata.longitude,
                props.document.property.latitude,
                props.document.property.longitude
            ) <= 0.25;
    }
    const address =
        (withInRadius
            ? props.document.property.stdAddress
            : metadata?.videoAddress) ||
        'Address is not available or not a valid address';

    const content = (
        <Content>
            <Row>
                <Label>DURATION:</Label>
                <Value>
                    {metadata.duration
                        ? `${moment.utc(metadata.duration).format('mm:ss')}`
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>TYPE:</Label>
                <Value>
                    {metadata.format ? metadata.format.toUpperCase() : noValue}
                </Value>
            </Row>
            <Row>
                <Label>MODEL:</Label>
                <Value>
                    {metadata.model
                        ? `${metadata.brand ?? ''} ${metadata.model}`
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>CREATE DATE:</Label>
                <Value>
                    {metadata.createDate
                        ? displayDate(
                              moment.tz(
                                  metadata.createDate,
                                  ['YYYY:MM:DD HH:mm:ss', moment.ISO_8601],
                                  timezoneId
                              ),
                              { invalidDisplay: 'N/A' }
                          )
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>TIME:</Label>
                <Value>
                    {metadata.createDate
                        ? displayTimeWithTimezone(
                              moment.tz(
                                  metadata.createDate,
                                  ['YYYY:MM:DD HH:mm:ss', moment.ISO_8601],
                                  timezoneId
                              )
                          )
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>VIDEO SIZE:</Label>
                <Value>
                    {metadata.height && metadata.width
                        ? `${metadata.height}X${metadata.width}`
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>GPS LATITUDE:</Label>
                <Value>
                    {metadata.latitude
                        ? toDMS(metadata.latitude, true)
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>GPS LONGITUDE:</Label>
                <Value>
                    {metadata.longitude
                        ? toDMS(metadata.longitude, false)
                        : noValue}
                </Value>
            </Row>
            <Row>
                <Label>ADDRESS:</Label>
                <Value style={{ width: 135, whiteSpace: 'normal' }}>
                    {address}
                </Value>
            </Row>
        </Content>
    );
    if (variant === 'drawer') {
        return (
            <ModalWrap>
                <ModalHeader>
                    <Title>Video Metadata</Title>
                    <IconButton
                        aria-label='Close'
                        onClick={props.handleCloseOnClick}
                        size='small'
                    >
                        <CloseIcon />
                    </IconButton>
                </ModalHeader>
                {content}
            </ModalWrap>
        );
    }

    return (
        <div>
            <Modal
                controlId='video-metadata-modal'
                onCloseClick={props.handleCloseOnClick}
                showClose
                closeByDocument
                header='Video Metadata'
                style={{ width: '320px', position: 'absolute' }}
                subHeader=''
            >
                {content}
            </Modal>
        </div>
    );
}
